import { useState, useCallback } from 'react';
import type { ReactElement } from 'react';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  TextField,
  Typography,
} from '@mui/material';
import { getCroppedImg, parseAspectParts } from 'app/utils/cropImage';

interface Props {
  open: boolean;
  imageSrc: string;
  aspectRatio?: string; // e.g. "4:3", "16:9"
  defaultFileName?: string;
  onClose: () => void;
  onCropDone: (file: File) => void;
  confirmLabel?: string;
}

export function ImageCropModal({
  open,
  imageSrc,
  aspectRatio,
  defaultFileName = 'cropped.jpg',
  confirmLabel = 'Crop & upload',
  onClose,
  onCropDone,
}: Props): ReactElement {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [filename, setFilename] = useState(defaultFileName);
  const [isCropping, setIsCropping] = useState(false);

  const aspectParts = parseAspectParts(aspectRatio);
  const aspectFloat = aspectParts ? aspectParts[0] / aspectParts[1] : undefined;

  const handleCropComplete = useCallback((_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  async function handleCrop() {
    if (!croppedAreaPixels || isCropping) return;
    setIsCropping(true);
    try {
      const file = await getCroppedImg(imageSrc, croppedAreaPixels, filename, aspectParts);
      onCropDone(file);
    } catch (error) {
      console.error('Crop failed:', error);
    } finally {
      setIsCropping(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Crop image</DialogTitle>
      <DialogContent className="vtwo:pb-0">
        <Box className="vtwo:relative vtwo:w-full vtwo:h-80 vtwo:bg-gray-900 vtwo:rounded-lg vtwo:overflow-hidden">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspectFloat}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={handleCropComplete}
          />
        </Box>
        <Box className="vtwo:px-1 vtwo:pt-4">
          <Typography variant="caption" color="text.secondary">
            Zoom
          </Typography>
          <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.1}
            onChange={(_, v) => setZoom(v as number)}
          />
        </Box>
        <TextField
          label="File name"
          value={filename}
          onChange={(e) => setFilename(e.target.value)}
          size="small"
          fullWidth
          className="vtwo:mt-2"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleCrop} disabled={isCropping}>
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
